import { useSelect } from 'downshift';
import { useRouter } from 'next/router';
import { useRef } from 'react';
import { getTranslatedSortingName } from '~/features/ProductList/components/helpers/filterHelper';
import ChevronDown from '~/icons/iconography/chevron/down.svg';
import { SortOptions } from '~/services/loop54/types';
import { Translate, useTranslations } from '~/services/umbraco/hooks';
import { useUrlQueryState } from '~/shared/hooks';
import { Text } from '../Text';
import { Button, ButtonLabel, ButtonValue, List, ListItem, Wrapper } from './styled';

type SortItem = SortOptions[keyof SortOptions] & {
    value?: string;
};

type Props = {
    items: SortOptions | undefined;
    defaultSort?: string;
    onSelectedItemsChange?: (selectedItemKey: string) => void;
};

const Sorting = ({ items, defaultSort, onSelectedItemsChange }: Props) => {
    const isAutoUpdate = useRef(false);
    const { isReady } = useRouter();
    const { translate } = useTranslations();
    const [urlQueryState, setUrlQueryState] = useUrlQueryState();

    const urlSortingParameter = urlQueryState?.sort?.[0] ?? defaultSort;

    const sortItemList = isReady
        ? mapSortOptions(items, translate)
        : getServerSideSortOption(translate);

    const initialSelectedItem =
        sortItemList.find((item) => urlSortingParameter && item.key === urlSortingParameter) ||
        sortItemList[0];

    const {
        isOpen,
        selectedItem,
        getToggleButtonProps,
        getLabelProps,
        getMenuProps,
        highlightedIndex,
        getItemProps,
    } = useSelect({
        items: sortItemList,
        id: 'sorting',
        onSelectedItemChange: ({ selectedItem }) => {
            if (selectedItem && !isAutoUpdate.current) {
                setUrlQueryState('sort', [selectedItem.key]);
                onSelectedItemsChange?.(selectedItem.key);
            }
            isAutoUpdate.current = false;
        },
        initialSelectedItem,
    });

    return (
        <Wrapper>
            <Button {...getToggleButtonProps()}>
                <ButtonLabel as="label" {...getLabelProps()}>
                    Sorter:
                </ButtonLabel>
                <ButtonValue>{selectedItem?.value}</ButtonValue>
                <ChevronDown />
            </Button>

            <List {...getMenuProps()}>
                {isOpen &&
                    sortItemList.map((item, index) => (
                        <ListItem
                            highlighted={highlightedIndex === index}
                            isSelected={selectedItem?.key === item.key}
                            key={`${item}${index}`}
                            {...getItemProps({ item, index })}
                        >
                            <Text>{item.value}</Text>
                        </ListItem>
                    ))}
            </List>
        </Wrapper>
    );
};

const getServerSideSortOption = (translate: Translate): SortItem[] => {
    return [
        {
            key: 'popularity',
            sorts: [{ type: 'popularity' }],
            value: getTranslatedSortingName('popularity', translate),
        },
    ];
};

const mapSortOptions = (options: SortOptions | undefined, translate: Translate): SortItem[] =>
    options
        ? Object.entries(options).map(([, { key, sorts }]) => ({
              key,
              sorts,
              value: getTranslatedSortingName(key, translate) || key,
          }))
        : [];

export default Sorting;
